import { action, observable, makeObservable } from 'mobx';
import { RootStore } from './root';
import { WithNetworkConcurrency } from './with-network-concurrency';
import { HttpMethod } from '../types';
import { NotificationManager } from '../components/notification-manager';

export type LabattGameAnalytics = {
    avgSessionTime: number;
    avgSessionsPerPlayer: number;
    nbPlayers: number;
    nbNewUsers: number;
    nbReturningPlayers: number;
    nbGamePlayed: number;
    couponsWon: number;
    couponsRedeemed: number;
    couponDetails: {
        type: string;
        won: number;
        redeemed: number;
        redemptionRate: number;
    }[];
    paddleWon: number;
    paddleClaimed: number;
    paddleRedemptionRate?: number;
    bagWon: number;
    bagClaimed: number;
    bagRedemptionRate: number;
    skiWon: number;
    skiClaimed: number;
    skiRedemptionRate: number;
    heaterWon: number;
    heaterClaimed: number;
    heaterRedemptionRate: number;
};

export type GameMetrics = {
    gameId: number;
    gameName: string;
    totalPlays: number;
    uniquePlayers: number;
    avgPlaysPerUser: number;
    rank?: number;
};

export type PlayCountDistribution = {
    gamesPlayed: number;
    userCount: number;
};

export type PerUserMetrics = {
    playCountDistribution: PlayCountDistribution[];
};

export type TierDistribution = {
    tier_1_user_count: number;
    tier_2_user_count: number;
    tier_3_user_count: number;
    total_users: number;
    tier_1_percentage: number;
    tier_2_percentage: number;
    tier_3_percentage: number;
};

export type TierProgression = {
    avg_days_to_tier_2?: string;
    avg_days_to_tier_3?: string;
};

export type TierCoupons = {
    tier_1_count: number;
    tier_2_count: number;
    tier_3_count: number;
    total_coupons: number;
};

export type GameMetricsResponse = {
    dateRange: {
        startDate: string;
        endDate: string;
    };
    perGame: GameMetrics[];
    perUser?: PerUserMetrics;
    global: {
        avgGamesPerUserPerDay: number;
        avgUsersHittingDailyCap?: number;
    };
    tiers?: {
        distribution?: TierDistribution;
        progression?: TierProgression;
        coupons?: TierCoupons;
    };
};

export type LabattGameSearchParams = {
    startDate: string;
    endDate: string;
};

export class LabattGameStore extends WithNetworkConcurrency {
    rootStore: RootStore;
    @observable analytics: LabattGameAnalytics | null = null;
    @observable previousAnalytics: LabattGameAnalytics | null = null;
    @observable isFetchingAnalytics = false;
    @observable gameMetrics: GameMetricsResponse | null = null;
    @observable isFetchingGameMetrics = false;

    constructor(rootStore: RootStore) {
        super();
        makeObservable(this);
        this.rootStore = rootStore;
    }

    private async fetchAnalytics(
        params: LabattGameSearchParams
    ): Promise<LabattGameAnalytics | null> {
        const response = await this.rootStore.makeNetworkCall<
            LabattGameAnalytics
        >({
            method: HttpMethod.GET,
            url: '/analytics/labatt',
            params,
        });

        if (!response.err) {
            return response.data;
        } else {
            NotificationManager.showError(
                'An error occurred while fetching the Labatt game analytics'
            );
            return null;
        }
    }

    @action
    async getLabattGameAnalytics(
        this: LabattGameStore,
        params: LabattGameSearchParams
    ) {
        this.isFetchingAnalytics = true;

        try {
            this.analytics = await this.fetchAnalytics(params);
        } finally {
            this.isFetchingAnalytics = false;
        }
    }

    @action
    async getPreviousPeriodAnalytics(
        this: LabattGameStore,
        params: LabattGameSearchParams
    ) {
        const start = new Date(params.startDate);
        const end = new Date(params.endDate);
        const periodLength = end.getTime() - start.getTime();
        const previousStartDate = new Date(start.getTime() - periodLength);
        const previousEndDate = new Date(end.getTime() - periodLength);

        const previousParams: LabattGameSearchParams = {
            startDate: previousStartDate.toISOString(),
            endDate: previousEndDate.toISOString(),
        };

        this.previousAnalytics = await this.fetchAnalytics(previousParams);
    }

    @action
    async getGameMetrics(
        this: LabattGameStore,
        params: LabattGameSearchParams
    ) {
        this.isFetchingGameMetrics = true;

        try {
            const response = await this.rootStore.makeNetworkCall<
                GameMetricsResponse
            >({
                method: HttpMethod.GET,
                url: '/analytics/labatt/games',
                params,
            });

            if (!response.err) {
                this.gameMetrics = response.data;
            } else {
                NotificationManager.showError(
                    'An error occurred while fetching game metrics'
                );
                this.gameMetrics = null;
            }
        } finally {
            this.isFetchingGameMetrics = false;
        }
    }
}
